import { ErrorMessage, HeapCustomEvent } from 'config';
import { floor } from 'utils/numbers';
import { enumerate } from 'utils/strings';

import CartItem from './CartItem';
import CartProduct from './CartProduct';
import CartCapacity from './CartCapacity';
import Plan, { PlanName } from './Plan';
import Alert, { AlertType } from './Alert';
import MealTypes from './MealTypes';
import FulfillmentStatus from './FulfillmentStatus';

import type { ICartItem, ReducedCartItem } from './CartItem';
import type { ProductsByMealType } from './MealTypes';
import Address from './Address';

// Links each rule to a condition that has to be met in the
// model. Validation fails if the condition evaluates to false.
// If two rules fail, the last failing one takes precedence.
enum ValidationRule {
  FREE_SHIPPING = 'hasFreeShipping',
  MAXIMUM_SELECTION = 'hasSelectableProducts',
  MAXIMUM_MEALS = 'hasSelectableMeals',
  MINIMUM_BASE_PRICE = 'hasMinimumBasePrice',
  MINIMUM_DINNERS = 'hasEnoughSelectedDinners',
}

type Validations = {
  minimum_dinners: number;
  maximum_meals: number;
  maximum_volume: number;
  minimum_base_price: number;
}

type Discounts = {
  discount_code: string;
  discount_type: 'dollar_off' | 'percentage_off';
  discount_value: number;
  constraints: {
    extras_only?: boolean;
    max_amount?: number;
    minimum_base_price?: number;
    cart_coupon_message?: string
    free_shipping: boolean;
  };
}

type ShippingFee = {
  amount: number;
  base: number;
  surcharge: number;
  shipping_fee: number;
  free_shipping_amount: number;
  tax: number;
}

interface Menu {
  menu_id: number;
  product_package_header: string;
  product_package_sub_header: string;
  start_at: Date;
  shipping_on: string;
}

interface PopupProduct {
  sku_id: number;
}

interface PackageProduct {
  sku_id: number;
}

interface IProgramMenuItems {
  Breakfast: ICartItem[];
  Lunch: ICartItem[];
  Dinner: ICartItem[];
}
interface ICart {
  id: number;
  menu_id: number;
  menu: Menu;
  is_thanksgiving_menu: boolean;
  fulfillment_status: FulfillmentStatus;
  delivery_on: Date;
  skip_deadline: Date;
  menu_items: ICartItem[];
  current_user_credits: number;
  discounts: Discounts;
  shipping_fee: ShippingFee;
  validations: Validations;
  plan: string | Plan;
  current_week_menu_cart: boolean;
  popup_products: PopupProduct[];
  package_products: PackageProduct[];
  program_menu_items: IProgramMenuItems;
  initialMenuItems?: ReducedCartItem[];
  isSummaryVisible?: boolean;
  last_added_by_recipe_tile?: boolean;
  skus_added_outside_widget?: PackageProduct[];
  alert?: Alert | null;
  capacity?: CartCapacity;

  readonly hasChanged?: boolean;
  readonly hasError?: boolean;
  readonly hasApplicableCredit?: boolean;
  readonly hasExtras?: boolean;
  readonly hasCoupon?: boolean;
  readonly hasGiveawayCredit?: boolean;
  readonly applicableCredit?: number;
  readonly couponCode?: string;
  readonly selectedItems?: CartItem[];
  readonly selectedProducts?: CartProduct[];
  readonly selectedItemCount?: number;
  readonly selectedDinnerCount?: number;
  readonly selectedMealCount?: number;
  readonly selectedSnackCount?: number;
  readonly selectedVolume?: number;
  readonly availableVolume?: number;
  readonly subtotalPrice?: number;
  readonly subtotalWithShippingPrice?: number;
  readonly shippingPrice?: number;
  readonly couponDiscount?: number;
  readonly totalDiscount?: number;
  readonly totalPrice?: number;
  readonly products?: CartProduct[];
  readonly productsByMealType?: ProductsByMealType;
  readonly hasFreeShipping?: boolean;
  readonly amountAwayFromFreeShipping?: number;
  readonly hasEnoughSelectedDinners?: boolean;
  readonly hasMinimumBasePrice?: boolean;
  readonly hasSelectableMeals?: boolean;
  readonly hasSelectableProducts?: boolean;
  readonly snacksToUpsell?: CartProduct[];
  readonly couponMessage?: string;
}

class Cart implements ICart {
  id: number;
  menu_id: number;
  menu: Menu;
  is_thanksgiving_menu: boolean;
  fulfillment_status: FulfillmentStatus;
  delivery_on: Date;
  skip_deadline: Date;
  menu_items: CartItem[];
  current_user_credits: number;
  discounts: Discounts;
  shipping_fee: ShippingFee;
  shipping_address: Address;
  validations: Validations;
  plan: Plan;
  current_week_menu_cart: boolean;
  popup_products: PopupProduct[];
  package_products: PackageProduct[];
  program_menu_items: IProgramMenuItems;
  initialMenuItems: ReducedCartItem[];
  isSummaryVisible = false;
  skus_added_outside_widget = [];
  last_added_by_recipe_tile = false;
  alert: Alert | null = null;
  capacity: CartCapacity;
  total: number;

  /**
   * Sets intance attributes and does initializarion tasks: Sorts menu_items,
   * stores their initial sate and aggregates their capacity.
   *
   * For maximum validation restrictions, sets them as Infinity if they are
   * undefined or null, effectively disabling them.
   *
   * If selectMinimumDinners === true, it removes any selection in cart and
   * selects only the minimum dinners (validation.minimum_dinners). Selected
   * ones will be the first in the menu_items array.
   */
  constructor(attributes: ICart, selectMinimumDinners = false) {
    Object.assign(this, attributes);

    const { maximum_meals, maximum_volume } = this.validations;

    this.plan = typeof this.plan === 'string'
      ? new Plan(attributes.plan as PlanName)
      : new Plan((attributes.plan as Plan).name);

    this.validations.maximum_volume = maximum_volume ?? Infinity;
    this.validations.maximum_meals = maximum_meals ?? Infinity;
    this.menu_items = this.menu_items ?? [];

    this.menu_items = this.menu_items.map((item) => new CartItem(item));

    if (selectMinimumDinners) this.selectMinimumDinners();

    this.storeInitialMenuItems();
    this.aggregateMenuItemCapacity();

    if (attributes.alert) {
      this.alert = attributes.alert;
    } else {
      this.validate();
    }
  }

  /**
   * Validates rules passed as parameter.
   *
   * If no validation rule is provided (i.e., rules array is empty) it will
   * validate all the rules in the order they were defined in ValidationRule
   * enum. Note that per requirements, we only display one error at a time,
   * so the error set by the last failing validation takes precedence and
   * overrides the previous ones.
   */
  validate(...rules: ValidationRule[]): void {
    const currentRules = !rules.length
      ? Object.keys(ValidationRule).map((key) => ValidationRule[key])
      : rules;

    this.alert = null;

    currentRules.forEach((rule) => {
      if (!this[rule]) {
        switch (rule) {
          case ValidationRule.FREE_SHIPPING: {
            this.alert = new Alert(
              AlertType.INFO,
              ValidationRule.FREE_SHIPPING,
              HeapCustomEvent.CART_FREE_SHIPPING_MESSAGE,
            );
            return;
          }

          case ValidationRule.MAXIMUM_SELECTION: {
            this.alert = new Alert(
              AlertType.INFO,
              ValidationRule.MAXIMUM_SELECTION,
            );
            return;
          }

          case ValidationRule.MAXIMUM_MEALS: {
            this.alert = new Alert(
              AlertType.INFO,
              ValidationRule.MAXIMUM_MEALS,
            );
            return;
          }

          case ValidationRule.MINIMUM_BASE_PRICE: {
            this.alert = new Alert(
              AlertType.ERROR,
              ValidationRule.MINIMUM_BASE_PRICE,
              HeapCustomEvent.CART_MIN_ORDER_VALUE_ERROR,
            );
            return;
          }

          case ValidationRule.MINIMUM_DINNERS: {
            this.alert = new Alert(
              AlertType.ERROR,
              ValidationRule.MINIMUM_DINNERS,
              HeapCustomEvent.CART_MIN_ORDER_VALUE_ERROR,
            );
            return;
          }

          default:
            this.alert = new Alert(
              AlertType.ERROR,
              // @ts-expect-error TODO: fix null safety
              null,
              null,
              ErrorMessage.UNKNOWN,
            );
        }
      }
    });
  }

  /**
   * Returns true if the current item quantity in the CartProduct can be
   * updated, taking into account sold out status, meal restrictions and
   * available volume capacity in this menu.
   */
  isUpdatable(cartProduct: CartProduct, isProgramItem = false): boolean {
    const cartItem = cartProduct.currentItem;

    if (isProgramItem) {
      // TODO: we should not have 2 different values for sold out
      return !cartItem.sold_out
          && !cartItem.isSoldOut
          && (cartItem.isSnack || this.capacity.meals > 0)
          && this.isCapacityAvailable(cartItem);
    }

    if (cartProduct.isSelected) {
      return true;
    }

    return !cartItem.isSoldOut
          && (cartItem.isSnack || this.capacity.meals > 0)
          && this.isCapacityAvailable(cartItem);
  }

  /**
   * Returns true if a single item of the CartProduct can be removed without
   * getting under the minimum base price or the minimum dinners threshold.
   */
  isUnitRemovable(cartProduct: CartProduct): boolean {
    const cartItem = cartProduct.currentItem;

    return (
      this.subtotalPrice - cartItem.unitPrice
        >= this.validations.minimum_base_price
        && (
          cartItem.isDinner
            ? this.selectedDinnerCount - 1 >= this.validations.minimum_dinners
            : true
        )
    );
  }

  /**
   * Returns true if all the quantity of the CartProduct can be removed without
   * getting under the minimum base price or the minimum dinners threshold.
   */
  isTotalRemovable(cartProduct: CartProduct): boolean {
    const cartItem = cartProduct.currentItem;

    return (
      this.subtotalPrice - (cartItem.unitPrice * cartItem.quantity)
        >= this.validations.minimum_base_price
        && (
          cartItem.isDinner
            ? this.selectedDinnerCount - cartItem.quantity
                >= this.validations.minimum_dinners
            : true
        )
    );
  }

  isCapacityAvailable(cartItem: CartItem): boolean {
    return this.capacity.volume - cartItem.skuVolume >= 0;
  }

  /**
   * Returns true if it's possible to switch to another serving option SKU in
   * the CartProduct taking into account the available volume capacity in this
   * menu.
   */
  areServingsUpdatable(cartProduct: CartProduct): boolean {
    const { hasServingOptions, highestServingItem } = cartProduct;
    return hasServingOptions && (highestServingItem.isSelected
        || this.capacity.volume - highestServingItem.skuVolume >= 0);
  }

  /**
   * Returns false if decreasing servings from the highest serving option SKU
   * in the CartProduct would make the cart subtotal go under the minimum base
   * price.
   */
  areServingsDecreasable(cartProduct: CartProduct): boolean {
    const {
      hasServingOptions,
      highestServingItem,
      lowestServingItem,
    } = cartProduct;

    const subtotalAfterDrecrease = this.subtotalPrice
        - highestServingItem.unitPrice
        + lowestServingItem.unitPrice;

    return hasServingOptions && (highestServingItem.isSelected
        && subtotalAfterDrecrease >= this.validations.minimum_base_price);
  }

  /**
   * Returns the maximum selectable quantity for a CartItem given the
   * meal restrictions and available volume capacity in this menu.
   */
  getSelectableQuantity(cartItem: CartItem): number {
    const { quantity, skuVolume } = cartItem;
    let { max_quantity: maxQuantity } = cartItem;

    if (!cartItem.isSnack) {
      maxQuantity = Math.min(maxQuantity, this.capacity.meals + quantity);
    }

    const total = floor(
      (this.availableVolume + (quantity * skuVolume)) / skuVolume,
      0,
    );

    return Math.min(maxQuantity, total);
  }

  /**
   * Returns the maximum selectable quantity for a CartItem given the
   * meal restrictions and available volume capacity in this menu.
   */
  getSelectableMinQuantity(cartItem: CartItem): number {
    let remainingTotal = this.subtotalPrice;
    const itemPrice = parseFloat(cartItem.sku.price);
    let loopCount = cartItem.quantity + 1;
    while (remainingTotal > 50 && loopCount > 1) {
      remainingTotal -= itemPrice;
      loopCount -= 1;
    }

    return loopCount;
  }

  getCartItem(skuId: number): CartItem {
    // @ts-expect-error TODO: fix null safety
    return this.menu_items.find((item) => item.sku.id === skuId);
  }

  storeInitialMenuItems(): void {
    if (this.initialMenuItems) return;

    this.initialMenuItems = this.menu_items.map(
      ({ sku, quantity }) => ({ skuId: sku.id, quantity }),
    );
  }

  aggregateMenuItemCapacity(): void {
    this.capacity = new CartCapacity(this);

    this.selectedItems.forEach(
      (cartItem) => this.capacity.reduce(cartItem),
    );
  }

  selectMinimumDinners(): void {
    let selectedDinners = 0;
    const { minimum_dinners: minDinners } = this.validations;

    this.menu_items.forEach((item) => {
      item.quantity = 0;

      if (item.isDinner && selectedDinners < minDinners) {
        item.quantity = 1;
        selectedDinners += 1;
      }
    });
  }

  get hasChanged(): boolean {
    return (
      this.menu_items.length !== this.initialMenuItems.length
        || this.menu_items.some((item, index) => (
          item.sku.id !== this.initialMenuItems[index].skuId
            || item.quantity !== this.initialMenuItems[index].quantity
        ))
    );
  }

  get hasError(): boolean {
    return this.alert?.type === AlertType.ERROR;
  }

  get hasApplicableCredit(): boolean {
    return !!this.current_user_credits;
  }

  get hasExtras(): boolean {
    const firstSelectedDinner = this
      .selectedItems
      .find((item) => item.sku.meal_type === 'dinner');
    // @ts-expect-error TODO: fix null safety
    const minimumVolume = firstSelectedDinner.skuVolume
        * this.validations.minimum_dinners;

    return this.selectedVolume > minimumVolume;
  }

  get hasCoupon(): boolean {
    return !!this.discounts.discount_code
        && this.discounts.discount_code !== 'giveaway';
  }

  get hasGiveawayCredit(): boolean {
    return !!this.discounts.discount_value
        && this.discounts.discount_code === 'giveaway';
  }

  get applicableCredit(): number {
    const discountedPrice = Math.max(
      this.subtotalWithShippingPrice - this.couponDiscount,
      0,
    );

    return this.current_user_credits >= discountedPrice
      ? discountedPrice
      : this.current_user_credits;
  }

  get couponCode(): string {
    return this.discounts.discount_code;
  }

  get selectedItems(): CartItem[] {
    return this.menu_items.filter((item) => item.quantity > 0);
  }

  get selectedProducts(): CartProduct[] {
    return Object
      .keys(this.productsByMealType)
      .reduce((acc, cur) => {
        // @ts-expect-error TODO: fix null safety
        acc.push(...this.productsByMealType[cur]);
        return acc;
      }, [])
    // @ts-expect-error TODO: fix null safety
      .filter((product) => product.isSelected);
  }

  get selectedItemCount(): number {
    return this.menu_items.reduce((acc, cur) => (acc + cur.quantity), 0);
  }

  get selectedDinnerCount(): number {
    return this.menu_items.reduce((acc, cur) => (
      cur.sku.meal_type === 'dinner' ? acc + cur.quantity : acc
    ), 0);
  }

  get selectedMealCount(): number {
    return this.menu_items.reduce((acc, cur) => (
      cur.sku.meal_type !== 'snack' ? acc + cur.quantity : acc
    ), 0);
  }

  get selectedSnackCount(): number {
    return this.menu_items.reduce((acc, cur) => (
      cur.sku.meal_type === 'snack' ? acc + cur.quantity : acc
    ), 0);
  }

  get selectedVolume(): number {
    return this.menu_items.reduce((acc, cur) => (acc + cur.skuVolume), 0);
  }

  get availableVolume(): number {
    return this.capacity.volume;
  }
  get subtotalPrice(): number {
    return this
      .selectedItems
      .reduce((acc, cur) => acc + cur.totalPrice, 0);
  }

  get subtotalWithShippingPrice(): number {
    return this.subtotalPrice + this.shippingPrice + this.extendedAreaSurchargePrice;
  }

  get extendedAreaSurchargePrice(): number {
    return this.shipping_fee.surcharge;
  }

  get shippingPrice(): number {
    if (this.shipping_fee.free_shipping_amount > this.subtotalPrice) {
      return this.shipping_fee.shipping_fee;
    }

    return 0;
  }
  get couponMessage():string {
    // @ts-expect-error TODO: fix null safety
    return this.discounts?.constraints?.cart_coupon_message;
  }

  get couponDiscount(): number {
    let couponDiscount = this.discounts.discount_code
      ? this.discounts.discount_value
      : 0;

    if (couponDiscount) {
      const { discount_type: couponType } = this.discounts;
      const {
        extras_only: isExtrasOnly,
        max_amount: maxAmount,
        minimum_base_price: minimumBasePrice,
      } = this.discounts.constraints;

      if (
        (minimumBasePrice && minimumBasePrice > this.subtotalPrice)
          || (isExtrasOnly && !this.hasExtras)
      ) {
        couponDiscount = 0;
      } else if (couponType === 'percentage_off') {
        couponDiscount = Math.min((this.subtotalPrice * couponDiscount) / 100, maxAmount ?? 0);
      } else {
        couponDiscount = Math.min(couponDiscount, this.subtotalPrice);
      }
    }

    return couponDiscount;
  }

  get totalDiscount(): number {
    const finalDiscount = this.hasGiveawayCredit
      ? this.discounts.discount_value
      : this.couponDiscount + this.applicableCredit;
    return Math.min(finalDiscount, this.subtotalWithShippingPrice);
  }

  get totalPrice(): number {
    return Math.max(
      this.subtotalWithShippingPrice - this.totalDiscount,
      0,
    );
  }

  get products(): CartProduct[] {
    return Object.values(this.menu_items.reduce((acc, item) => {
      const group = `_${item.sku.product_id}`;

      acc[group] = acc[group] || new CartProduct(this.plan.servings);
      acc[group].add(item);

      return acc;
    }, {})) as CartProduct[];
  }

  filteredProductsByMealType(
    filter: string,
    subFilter: string,
    preferenceFilters: string[],
    isRteDaypartFiltersEnabled: boolean,
  ): ProductsByMealType {
    const productsByMealType = MealTypes
      .getFilteredProductsByMealType(filter, subFilter, isRteDaypartFiltersEnabled);
    this.products.forEach((product) => {
      const dayPart = product.items[0].day_part;
      const {
        plan_group: planGroup,
        meal_type: mealType,
        tags,
        is_lto_component,
        is_lto_box,
      } = product.items[0].sku;
      if (is_lto_component || is_lto_box) {
        return;
      }

      // If no preference filters are selected, then tags are irrelevant. In that case, we set
      // the value to ['all'] so that products with 0 tags are still pushed to productsByMealType.
      const applicableTags = preferenceFilters.length
        ? tags.filter((tag) => preferenceFilters.includes(tag.name))
        : ['all'];

      let key = mealType;
      if (planGroup === 'prepared_one_serving') {
        key = isRteDaypartFiltersEnabled ? `rte_${dayPart?.toLowerCase() || 'dinner'}` : 'ready_to_eat';
      }

      if (mealType === 'snack') {
        key = 'grocery';
      }

      if (key in productsByMealType && applicableTags.length) {
        productsByMealType[key].push(product);
      }
    });

    if ('less_prep' in productsByMealType) {
      this.products.forEach((product) => {
        const { tags } = product.items[0].sku;

        const less_prep_tags = tags.filter((tag) => tag.name.toLowerCase() === 'less prep');
        if (less_prep_tags.length > 0) {
          // @ts-expect-error TODO: fix null safety
          productsByMealType.less_prep.push(product);
        }
      });
    }

    Object.keys(productsByMealType).forEach((key) => {
      if (!productsByMealType[key].length) {
        delete productsByMealType[key];
      }
    });

    return productsByMealType;
  }

  get productsByMealType(): ProductsByMealType {
    const productsByMealType = MealTypes
      .getEmptyProductsByMealType(this.plan.name);

    this.products.forEach((product) => {
      const { plan_group: planGroup } = product.items[0].sku;
      let { meal_type: mealType } = product.items[0].sku;

      if (planGroup === 'prepared_one_serving') {
        mealType = 'prepared';
      }

      productsByMealType[mealType].push(product);
    });

    MealTypes.keys().forEach((key) => {
      if (!productsByMealType[key].length) {
        delete productsByMealType[key];
      }
    });

    return productsByMealType;
  }

  get productsToUpsell(): CartProduct[] {
    const upsellProducts: CartProduct[] = [];

    // Add popup products that are not selected by the user
    this.popup_products.some((popupProduct) => {
      const snack = this.products.find(
        (p) => p.hasSku(popupProduct.sku_id),
      );

      if (snack && !snack.isSelected) {
        upsellProducts.push(snack);
      }

      return upsellProducts.length === 3;
    });

    // Remove duplicates (different sku ids can pertain to the same Poduct)
    const uniqueUpsellProducts: CartProduct[] = [];

    upsellProducts.forEach((product) => {
      if (!uniqueUpsellProducts.some((prod) => product.equals(prod))) {
        uniqueUpsellProducts.push(product);
      }
    });

    // Fill in any missing slot with non-selected AND non-soldOut products
    if (uniqueUpsellProducts.length < 3) {
      this?.productsByMealType?.snack?.some((product) => {
        const isAlreadyAdded = uniqueUpsellProducts.some(
          (p) => p.productId === product.productId,
        );

        if (!product.isSoldOut && !product.isSelected && !isAlreadyAdded) {
          uniqueUpsellProducts.push(product);
        }

        return uniqueUpsellProducts.length === 3;
      });
    }

    return uniqueUpsellProducts;
  }

  get hasNonSnackProductsToUpsell(): boolean {
    const itemsToUpsell = this.menu_items.filter(
      ({ sku }) => this.popup_products.some(
        ({ sku_id }) => sku_id === sku.id,
      ),
    );

    return itemsToUpsell.some((item) => !item.isSnack);
  }

  get upsellingMealTypeSummary(): string {
    return this.upsellingMealTypeSummaryWrapper('plantry items');
  }

  get newNonmaclatureUpsellingMealTypeSummary(): string {
    return this.upsellingMealTypeSummaryWrapper('grocery items');
  }

  upsellingMealTypeSummaryWrapper(groceryCategory:string): string {
    const categories = {};
    const mealStrings = [];

    this.productsToUpsell.forEach((product) => {
      let category = groceryCategory;

      if (product.currentItem.isPreparedMeal) {
        category = 'ready to eat dishes';
      }

      if (product.currentItem.isMealKit) {
        category = 'meal kits';
      }

      categories[category] = true;
    });
    // @ts-expect-error TODO: fix null safety
    Object.keys(categories).forEach((key) => mealStrings.push(key));

    return enumerate(mealStrings);
  }

  get hasFreeShipping(): boolean {
    return this.shippingPrice === 0 || (this.discounts?.constraints?.free_shipping ?? false);
  }

  get amountAwayFromFreeShipping(): number {
    return Math.max(
      this.shipping_fee.free_shipping_amount - this.subtotalPrice,
      0,
    );
  }

  get hasEnoughSelectedDinners(): boolean {
    return this.selectedDinnerCount >= this.validations.minimum_dinners;
  }

  get hasMinimumBasePrice(): boolean {
    return this.subtotalPrice >= this.validations.minimum_base_price;
  }

  get hasSelectableMeals(): boolean {
    return this.selectedMealCount < this.validations.maximum_meals;
  }

  get hasSelectableProducts(): boolean {
    return this.products.some((item) => {
      if (item.isSelected) {
        const { currentItem } = item;
        const { quantity } = currentItem;

        return item.currentItem.isMultipleSelectionAllowed
          ? this.getSelectableQuantity(currentItem) > quantity
          : false;
      }

      return this.isUpdatable(item);
    });
  }
}

export type { ICart };
export { ValidationRule };
export default Cart;
